import { FunctionComponent, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import GridItem from '../../layout/GridComponents/GridItem';
import CustomTable from '../../tables/CustomTable';
import { CustomColumn } from '../../../types/components/tables/tableTypes';
import { useMMFData } from './services/queries';
import { QueryBoundary } from '../../utilityComponents/QueryBoundary';
import usePngFromRecharts from '../../../hooks/usePngFromRecharts';
import { useDispatch } from 'react-redux';
import {
  addComponentToPdfExport,
  setPdfAnalysisDate,
  clearPdfAnalysisDate,
} from '../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../types/redux/pdfExports/FileExportsStore';
import { mainColors } from '../../../styling/theme';
import { FundInfoComponentProps } from '../../layout/general/SimpleGeneralPageWrapper';

// New data structure interfaces
export interface MetricData {
  value: number | string;
  unit: string;
  limit: string;
  passed: boolean;
  description: string;
}

export interface LiquiditySection {
  daily_maturing_assets: MetricData;
  weekly_maturing_assets: MetricData;
}

export interface MarketRiskSection {
  weighted_average_maturity: MetricData;
  weighted_average_maturity_mmf: MetricData;
  weighted_average_life: MetricData;
  residual_maturity: MetricData;
  duration: MetricData;
}

export interface CreditSection {
  weighted_average_credit: MetricData;
  hy_exposure: MetricData;
}

export interface TimeSeriesMetric {
  '1mo': MetricData;
  '3mo': MetricData;
  '1yr': MetricData;
}

export interface ReturnsData {
  '1yr': number;
  '3mo': number;
  '1mo': number;
  unit: string;
}

export interface VolatilityData {
  '1yr': number;
  '3mo': number;
  '1mo': number;
  unit: string;
}

export interface TimeSeriesAnalysis {
  correlation: TimeSeriesMetric;
  tracking_error: TimeSeriesMetric;
  returns: {
    fund: ReturnsData;
    mmf: ReturnsData;
  };
  volatility: {
    fund: VolatilityData;
    mmf: VolatilityData;
  };
}

export interface Category {
  key: string;
  label: string;
}

export interface Metadata {
  fund_name: string;
  fund_name_full: string;
  position_date: string;
  base_currency: string;
  nav: number;
  mmf_compatibility_score: number;
  mmf_status: string;
  total_criteria: number;
  passed_criteria: number;
  selected_position_date?: string;
  returned_position_date?: string;
}

export interface TimeSeriesChartData {
  series: Array<{
    date: string;
    sub_fund: number;
    mmf_proxy: number;
  }>;
  description: string;
  x_axis: string;
  y_axis: string;
  fund_label: string;
  mmf_label: string;
}

// Main type
export interface MoneyMarketData {
  metadata: Metadata;
  liquidity: LiquiditySection;
  market_risk: MarketRiskSection;
  credit: CreditSection;
  correlation: TimeSeriesChartData;
  tracking_error: TimeSeriesChartData;
  returns: TimeSeriesChartData;
  time_series_chart_data: TimeSeriesChartData;
}

// Table data interfaces
export interface FinancialMetricsTableData {
  section: string;
  type: string;
  limit: string;
  value: string | number;
  unit: string;
  status: string;
  passed: boolean;
}

export interface HistoricalDataTableData {
  date: string;
  sub_fund: number;
  mmf_proxy: number;
}

// Column definitions for Financial Metrics table
const financialMetricsColumns: CustomColumn<FinancialMetricsTableData>[] = [
  {
    title: 'Section',
    field: 'section',
    width: '15%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
  },
  {
    title: 'Type',
    field: 'type',
    width: '25%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
  },
  {
    title: 'MMF Limit',
    field: 'limit',
    width: '25%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
  },
  {
    title: 'Value',
    field: 'value',
    width: '20%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
  },
  {
    title: 'Status',
    field: 'status',
    width: '15%',
    headerStyle: { textAlign: 'center' },
    cellStyle: { textAlign: 'center', fontWeight: 'bold' },
    pdfRenderType: 'CustomBackgroundColor',
    render: (rowData) => {
      return (
        <StatusCell
          cellColor={mapStatusToColor(rowData.passed)}
          value={rowData.status}
          passed={rowData.passed}
        />
      );
    },
  },
];

interface StatusCellProps {
  cellColor: string;
  value: string;
  passed?: boolean; // Add passed prop to determine UI styling
}

const StatusCell: React.FC<StatusCellProps> = (props) => {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-2 w-2 rounded-full ${
          props.passed ? 'bg-alert' : 'bg-pass'
        }`}
      />
      <span
        className={`inline-block rounded-full border px-2 py-1 text-xs font-medium ${
          props.passed
            ? 'border-alert/40 bg-alert/10 text-alert'
            : 'border-pass/40 bg-pass/10 text-pass'
        }`}
      >
        {props.value}
      </span>
    </div>
  );
};

const mapStatusToColor = (passed: boolean): string => {
  return passed ? mainColors.Alert : mainColors.Pass; // Orange for MMF Compatible, Green for Not MMF
};

const getStatusText = (passed: boolean) => {
  return passed ? 'MMF Compatible' : 'Not MMF';
};

const formatValue = (value: string | number, unit: string) => {
  if (typeof value === 'number') {
    return `${value.toFixed(2)} ${unit}`;
  }
  return `${value} ${unit}`;
};

// Column definitions for Historical Data table
const historicalDataColumns: CustomColumn<HistoricalDataTableData>[] = [
  {
    title: 'Date',
    field: 'date',
    width: '40%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
  },
  {
    title: 'Fund',
    field: 'sub_fund',
    width: '30%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
    render: (rowData) => (rowData.sub_fund || 0).toFixed(2),
  },
  {
    title: 'MMF Proxy',
    field: 'mmf_proxy',
    width: '30%',
    headerStyle: { textAlign: 'left' },
    cellStyle: { textAlign: 'left' },
    render: (rowData) => (rowData.mmf_proxy || 0).toFixed(4),
  },
];

const MoneyMarketMonitorPage: FunctionComponent<FundInfoComponentProps> = ({
  positionDate,
  fundId,
}) => {
  const dispatch = useDispatch();
  const { ref, handleDownload } = usePngFromRecharts();

  const { data, isLoading, error, isFetching } = useMMFData(
    fundId,
    positionDate,
  );

  // Fund status table columns - will be created inside the component where data is available
  const createFundStatusColumns = (fundName: string): CustomColumn<any>[] => [
    {
      title: fundName,
      field: 'metric',
      width: '50%',
      headerStyle: { textAlign: 'left' },
      cellStyle: { textAlign: 'left' },
    },
    {
      title: '',
      field: 'value',
      width: '50%',
      headerStyle: { textAlign: 'center' },

      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      render: (rowData) => {
        if (rowData.metric === 'MMF Status') {
          const isPassed = rowData.value === 'Not MMF';
          return (
            <StatusCell
              cellColor={mapStatusToColor(!isPassed)}
              value={rowData.value}
              passed={!isPassed}
            />
          );
        }
        // For all other fields, return just the string value
        return rowData.value;
      },
    },
  ];

  useEffect(() => {
    if (data) {
      // Set the analysis date for PDF export
      if (data.metadata?.returned_position_date) {
        dispatch(setPdfAnalysisDate(data.metadata.returned_position_date));
      }

      // Add fund status table to PDF export
      dispatch(
        addComponentToPdfExport({
          positionDate: data.metadata?.returned_position_date,
          identifier: `${fundId}_fund_status`,
          handler: async () => {
            const fundStatusData = createFundStatusTableData(data);
            const fundStatusColumns = createFundStatusColumns(
              data.metadata?.fund_name_full,
            );
            const renderColumns = fundStatusColumns.map((col) => {
              // Only apply CustomBackgroundColor to the value column for MMF Status
              if (col.field === 'value') {
                return {
                  header: col.title,
                  dataKey: col.field,
                  headerStyle: col.headerStyle,
                  cellStyle: col.cellStyle,
                  pdfRenderType: 'CustomBackgroundColor',
                  render: col.render,
                };
              }
              return {
                header: col.title,
                dataKey: col.field,
                headerStyle: col.headerStyle,
                cellStyle: col.cellStyle,
              };
            });

            return {
              startY: 50,
              columns: renderColumns,
              body: fundStatusData,
            };
          },
          type: PdfComponentType.TABLE,
          title: 'Fund Status Overview',
          groupOrder: 1,
          groupName: 'mma_metrics_page',
          specifiedWidth: 12,
          tablesInRow: 1,
          dontMoveToNewPage: true,
        }),
      );

      // Add historical comparison chart to PDF export
      dispatch(
        addComponentToPdfExport({
          positionDate: data.metadata?.returned_position_date,
          identifier: `${fundId}_historical_comparison`,
          handler: handleDownload,
          type: PdfComponentType.GENERAL_COMPONENT_IMAGE,
          title: 'Historical Comparison',
          groupOrder: 3,
          groupName: 'mma_metrics_page',
        }),
      );
    }
  }, [data, ref, fundId, dispatch, handleDownload]);

  useEffect(() => {
    // CLEAR IT
    return () => {
      dispatch(clearPdfAnalysisDate());
    };
  }, [dispatch]);

  // Create fund status table data
  const createFundStatusTableData = (mmfData: MoneyMarketData) => {
    return [
      {
        metric: 'Fund Name',
        value: mmfData.metadata?.fund_name_full || 'N/A',
      },
      {
        metric: 'MMF Status',
        value: mmfData.metadata?.mmf_status || 'N/A',
      },
      {
        metric: 'Position Date',
        value: mmfData.metadata?.returned_position_date || 'N/A',
      },

      {
        metric: 'MMF Compatibility Score',
        value: mmfData.metadata?.mmf_compatibility_score?.toFixed(2) || 'N/A',
      },

      {
        metric: 'Criteria Passed',
        value: `${mmfData.metadata?.passed_criteria || 0}/${mmfData.metadata?.total_criteria || 0}`,
      },
      {
        metric: 'Base Currency',
        value: mmfData.metadata?.base_currency || 'N/A',
      },
      {
        metric: 'NAV',
        value: mmfData.metadata?.nav?.toFixed(2) || 'N/A',
      },
    ];
  };

  // Transform data for Financial Metrics table
  const transformFinancialMetricsData = (
    mmfData: MoneyMarketData,
  ): FinancialMetricsTableData[] => {
    const tableData: FinancialMetricsTableData[] = [];

    // Liquidity Section
    if (mmfData.liquidity) {
      Object.entries(mmfData.liquidity).forEach(([key, metric]) => {
        tableData.push({
          section: 'Liquidity',
          type: metric.description,
          limit: metric.limit,
          value: formatValue(metric.value, metric.unit),
          unit: metric.unit,
          status: getStatusText(metric.passed),
          passed: metric.passed,
        });
      });
    }

    // Market Risk Section
    if (mmfData.market_risk) {
      Object.entries(mmfData.market_risk).forEach(([key, metric]) => {
        tableData.push({
          section: 'Market Risk',
          type: metric.description,
          limit: metric.limit,
          value: formatValue(metric.value, metric.unit),
          unit: metric.unit,
          status: getStatusText(metric.passed),
          passed: metric.passed,
        });
      });
    }

    // Credit Section
    if (mmfData.credit) {
      Object.entries(mmfData.credit).forEach(([key, metric]) => {
        tableData.push({
          section: 'Credit',
          type: metric.description,
          limit: metric.limit,
          value: formatValue(metric.value, metric.unit),
          unit: metric.unit,
          status: getStatusText(metric.passed),
          passed: metric.passed,
        });
      });
    }

    // Correlation Section
    if (mmfData.correlation) {
      Object.entries(mmfData.correlation).forEach(([period, metric]) => {
        tableData.push({
          section: 'Correlation',
          type: metric.description,
          limit: metric.limit,
          value: formatValue(metric.value, metric.unit),
          unit: metric.unit,
          status: getStatusText(metric.passed),
          passed: metric.passed,
        });
      });
    }

    // Tracking Error Section
    if (mmfData.tracking_error) {
      Object.entries(mmfData.tracking_error).forEach(([period, metric]) => {
        tableData.push({
          section: 'Tracking Error',
          type: metric.description,
          limit: metric.limit,
          value: formatValue(metric.value, metric.unit),
          unit: metric.unit,
          status: getStatusText(metric.passed),
          passed: metric.passed,
        });
      });
    }

    return tableData;
  };

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={!!error}
      error={error}
      data={data}
      loadingMessages={[
        'Loading MMF data...',
        'Analyzing fund metrics...',
        'Preparing compliance report...',
      ]}
      errorMessage="Unable to load Money Market Fund data. Please contact support."
      noDataMessage="No MMF data available for the selected fund and date."
    >
      {(mmfData) => {
        const financialMetricsData = transformFinancialMetricsData(mmfData);

        return (
          <GridItem xs={12}>
            {/* Status Summary Section */}
            <div className="mb-4 rounded-lg bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`h-4 w-4 rounded-full ${
                      mmfData.metadata?.mmf_status === 'Not MMF'
                        ? 'bg-pass'
                        : 'bg-alert'
                    }`}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Overall Fund Status:
                  </span>
                  <span
                    className={`inline-block rounded-full border px-3 py-1 text-sm font-semibold ${
                      mmfData.metadata?.mmf_status === 'Not MMF'
                        ? 'border-pass/40 bg-pass/10 text-pass'
                        : 'border-alert/40 bg-alert/10 text-alert'
                    }`}
                  >
                    {mmfData.metadata?.mmf_status || 'Unknown'}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({mmfData.metadata?.passed_criteria || 0}/
                    {mmfData.metadata?.total_criteria || 0} criteria passed)
                  </span>
                </div>
                {mmfData.metadata?.returned_position_date && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      Returned position date:
                    </span>
                    <span className="inline-block rounded-full border border-blue-200 bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
                      {mmfData.metadata.returned_position_date}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Financial Metrics Table */}
              <GridItem card className="lg:col-span-2">
                <CustomTable<FinancialMetricsTableData>
                  data={financialMetricsData}
                  columns={financialMetricsColumns}
                  showToolbar={true}
                  options={{
                    paging: false,
                    exportButton: true,
                    search: true,
                  }}
                  multipleTablePdfParameters={{
                    groupOrder: 2,
                    groupName: 'mma_metrics_page',
                    specifiedWidth: 12,
                    tablesInRow: 1,
                  }}
                  title="Financial Metrics"
                  exportFileName="financial_metrics"
                  id="financial-metrics-table"
                />
              </GridItem>

              {/* Historical Data Table */}
              <GridItem card className="lg:col-span-1">
                <div className="max-h-[978px] overflow-y-auto">
                  <CustomTable<HistoricalDataTableData>
                    data={mmfData.time_series_chart_data?.series || []}
                    options={{
                      paging: false,
                      exportButton: true,
                      search: true,
                    }}
                    columns={historicalDataColumns}
                    showToolbar={true}
                    title="Historical Data"
                    exportFileName="historical_data"
                    id="historical-data-table"
                    noExport
                  />
                </div>
              </GridItem>
            </div>

            {/* Historical Comparison Chart */}
            <div className="mt-4">
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Historical Comparison
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {mmfData.time_series_chart_data?.description ||
                      'Fund vs MMF Proxy Performance Over Time'}
                  </p>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={mmfData.time_series_chart_data?.series || []}
                      ref={ref}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        stroke="#6b7280"
                        fontSize={12}
                        tickFormatter={(value) =>
                          new Date(value).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })
                        }
                      />
                      <YAxis
                        yAxisId="left"
                        stroke="#3b82f6"
                        fontSize={12}
                        domain={[99.8, 101.2]}
                        tickFormatter={(value) => value.toFixed(1)}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke="#f97316"
                        fontSize={12}
                        domain={[102.0, 103.0]}
                        tickFormatter={(value) => value.toFixed(1)}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          value.toFixed(4),
                          name,
                        ]}
                        labelFormatter={(label) => {
                          const date = new Date(label);
                          return date.toISOString().split('T')[0];
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="sub_fund"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name={
                          mmfData.time_series_chart_data?.fund_label ||
                          'Fund Value'
                        }
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="mmf_proxy"
                        stroke="#f97316"
                        strokeWidth={2}
                        name={
                          mmfData.time_series_chart_data?.mmf_label ||
                          'MMF Proxy'
                        }
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </GridItem>
        );
      }}
    </QueryBoundary>
  );
};

export default MoneyMarketMonitorPage;
